import { Alert } from '@mui/material';
import { AxiosError } from 'axios';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { map, values } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  CloseFormikDialogResult,
  CloseDialogFormikHandler,
  Dialog,
  FormControls,
  FormikNumericField,
  FormikYesNoRadioGroup,
  FormikSelect,
  Money,
} from 'components';
import { DISPUTE_TO_COMPLETED_ORDER_STATUS_REASONS } from 'constants/order.constants';
import { OrderStatus, OrderStatusDetails, OrderStatusReason } from 'enums';
import {
  useCurrencies,
  useMeta,
  usePrevious,
  useUser,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { PayinOrder, ResolvePayinOrderDispute } from 'types';
import { orderUtils, validationUtils } from 'utils';

type Values = ResolvePayinOrderDispute;

type Props = {
  open: boolean;
  order: PayinOrder;
  error?: AxiosError | null;
  amountDisabled: boolean;
  onClose: CloseDialogFormikHandler<Values>;
};

export const ResolveDisputeDialog: React.FC<Props> = ({
  open,
  order,
  error,
  amountDisabled,
  onClose,
}) => {
  const { isManager, isTrader } = useUser();
  const { t: tCommon } = useTranslation();
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.resolve',
  });
  const prevOpen = usePrevious(open);
  const { getFiatCurrencySymbol } = useCurrencies();
  const isTraderDispute = useMemo(() => !order.p2pProviderOrderId, [order]);
  const [initialValues, setInitialValues] = useState<Values>({
    status: OrderStatus.Completed,
    amount: order.amount,
    ...(isTraderDispute && { statusReason: '' as OrderStatusReason }),
  });
  const { orderStatusReasons } = useUserContext();

  const validationSchema: Yup.ObjectSchema<Values> = useMemo(
    () =>
      Yup.object().shape({
        status: Yup.mixed<OrderStatus>()
          .oneOf([OrderStatus.Completed, OrderStatus.Cancelled])
          .required(tCommon('errors.required')),
        amount: Yup.number()
          .moreThan(0, tCommon('errors.natural_number'))
          .required(tCommon('errors.required')),
        statusReason: Yup.mixed<OrderStatusReason>()
          .oneOf(values(OrderStatusReason))
          .optional(),
      }),
    [tCommon],
  );

  const currencySymbol = useMemo(
    () => getFiatCurrencySymbol(order?.fiatCurrencyId),
    [order, getFiatCurrencySymbol],
  );

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<Values>) => {
      if (!result.ok) {
        result.data?.formikHelpers.resetForm();
      }
      onClose(result);
    },
    [onClose],
  );

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      handleClose({ ok: true, data: { values, formikHelpers } });
    },
    [handleClose],
  );

  const renderOrderStatus = useCallback(
    (status: string) => (
      <span>
        {`${t('resolve_as.prefix')} `}
        <span className="tw-font-bold">{status}</span>
      </span>
    ),
    [t],
  );

  const getStatusReasonOptions = useCallback(
    (status: OrderStatus) => {
      let statuses: OrderStatusReason[] = [];
      if (status === OrderStatus.Completed) {
        statuses = DISPUTE_TO_COMPLETED_ORDER_STATUS_REASONS;
      } else if (status === OrderStatus.Cancelled) {
        statuses = orderStatusReasons;
      }
      return map(statuses, (status) => ({
        label: orderUtils.getStatusReasonLabel(status),
        value: status,
      }));
    },
    [orderStatusReasons],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  const handleStatusChange = useCallback(() => {
    formik.setFieldValue('statusReason', '');
  }, [formik]);

  useEffect(() => {
    if (open && !prevOpen) {
      setInitialValues({
        status: OrderStatus.Completed,
        amount: order.amount,
        ...(!order.p2pProviderOrderId
          ? { statusReason: '' as OrderStatusReason }
          : {}),
      });
    }
  }, [open, prevOpen, order]);

  useEffect(() => {
    if (error) {
      formik.setErrors(
        validationUtils.getFormErrors(error, {
          amount: { too_big: tCommon('errors.not_enough_money') },
        }),
      );
    } else {
      formik.setErrors({});
    }
  }, [tCommon, formik, error]);

  const { traderCanCancelDispute } = useMeta();
  const disputeCancelAvailable = useMemo(
    () =>
      isManager ||
      (isTrader &&
        traderCanCancelDispute &&
        !(
          order.statusDetails === OrderStatusDetails.DisputeRevertCancelled &&
          order.previousAmount
        )),
    [isManager, isTrader, traderCanCancelDispute, order],
  );

  return (
    <FormikProvider value={formik}>
      <Dialog
        open={open}
        title={t('title_dispute')}
        data={{ values: formik.values, formikHelpers: formik }}
        onClose={handleClose}
      >
        <Form>
          <Alert severity="info" sx={{ mb: 4, py: 0 }}>
            {formik.values.status === OrderStatus.Completed &&
              renderOrderStatus(t('resolve_as.completed'))}
            {disputeCancelAvailable &&
              formik.values.status === OrderStatus.Cancelled &&
              renderOrderStatus(t('resolve_as.cancelled'))}
          </Alert>
          {!!order.disputeAmount && (
            <div className="tw-mb-4">
              {`${tCommon('features.orders.table.columns.dispute_amount')}: `}
              <Money
                value={order.disputeAmount}
                fiatCurrencyId={order.fiatCurrencyId}
                symbol
              />
            </div>
          )}
          <FormControls>
            {disputeCancelAvailable && (
              <FormikYesNoRadioGroup
                name="status"
                label={t('payment_received')}
                yesValue={OrderStatus.Completed}
                noValue={OrderStatus.Cancelled}
                onChange={handleStatusChange}
              />
            )}
            {!order.p2pProviderOrderId && (
              <FormikSelect
                name="statusReason"
                label={t('reason')}
                options={getStatusReasonOptions(formik.values.status)}
                noneOption
                disablePortal={false}
              />
            )}
            {formik.values.status === OrderStatus.Completed && (
              <FormikNumericField
                name="amount"
                disabled={amountDisabled}
                label={`${t('received_amount')} (${currencySymbol})`}
                allowNegative={false}
                required
                fullWidth
              />
            )}
          </FormControls>
        </Form>
      </Dialog>
    </FormikProvider>
  );
};
